import React from 'react';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import {
  Description as SoapIcon,
  Medication as MedIcon,
  Vaccines as VaccineIcon,
  Healing as ProcedureIcon,
  Science as LabIcon,
  Note as NoteIcon,
  Phone as CommIcon,
  Article as RecordIcon,
  MoreVert as MoreVertIcon,
  Archive as ArchivedIcon,
  LocalHospital as DischargeIcon,
  Receipt as InvoiceIcon,
  Send as StableDocsIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, isToday, isYesterday, isValid } from 'date-fns';
import { formatCalendarDate } from '../../../utils/dateUtils';

export type TimelineKind =
  | 'soap'
  | 'medication'
  | 'vaccination'
  | 'procedure'
  | 'lab'
  | 'visit_note'
  | 'record'
  | 'communication'
  | 'discharge'
  | 'invoice'
  | 'stabledocs';

export interface TimelineEvent {
  id: string;
  kind: TimelineKind;
  date: string;
  title: string;
  summary?: string;
  by?: string;
  isArchived?: boolean;
  raw: any;
}

const KIND_META: Record<
  TimelineKind,
  { label: string; color: string; bg: string; icon: React.ReactElement }
> = {
  soap: { label: 'SOAP', color: '#1565C0', bg: '#E3F2FD', icon: <SoapIcon sx={{ fontSize: 16 }} /> },
  medication: { label: 'Rx', color: '#2E7D32', bg: '#E8F5E9', icon: <MedIcon sx={{ fontSize: 16 }} /> },
  vaccination: { label: 'Vax', color: '#6A1B9A', bg: '#F3E5F5', icon: <VaccineIcon sx={{ fontSize: 16 }} /> },
  procedure: { label: 'Proc', color: '#C62828', bg: '#FFEBEE', icon: <ProcedureIcon sx={{ fontSize: 16 }} /> },
  lab: { label: 'Lab', color: '#EF6C00', bg: '#FFF3E0', icon: <LabIcon sx={{ fontSize: 16 }} /> },
  visit_note: { label: 'Visit', color: '#455A64', bg: '#ECEFF1', icon: <NoteIcon sx={{ fontSize: 16 }} /> },
  record: { label: 'Note', color: '#5D4037', bg: '#EFEBE9', icon: <RecordIcon sx={{ fontSize: 16 }} /> },
  communication: { label: 'Comm', color: '#00838F', bg: '#E0F7FA', icon: <CommIcon sx={{ fontSize: 16 }} /> },
  discharge: { label: 'Discharge', color: '#4527A0', bg: '#EDE7F6', icon: <DischargeIcon sx={{ fontSize: 16 }} /> },
  invoice: { label: 'Invoice', color: '#1565C0', bg: '#E3F2FD', icon: <InvoiceIcon sx={{ fontSize: 16 }} /> },
  stabledocs: { label: 'StableDocs', color: '#9A5A1E', bg: '#FBEFE3', icon: <StableDocsIcon sx={{ fontSize: 16 }} /> },
};

export const kindMeta = (kind: TimelineKind) => KIND_META[kind] ?? KIND_META.record;

const firstLine = (s?: string, max = 140) => {
  if (!s) return '';
  const line = s.split(/\r?\n/).find((l) => l.trim()) ?? '';
  const cleaned = line.replace(/\s+/g, ' ').trim();
  return cleaned.length > max ? `${cleaned.slice(0, max).trim()}…` : cleaned;
};

/**
 * Parse a date defensively. Timeline records come from heterogeneous sources
 * (`raw: any`) where a date field can be missing or malformed; date-fns
 * format()/formatDistanceToNow() throw RangeError on an Invalid Date, which
 * would white-screen the whole chart. Returns null for anything unparseable
 * so callers can render a placeholder instead.
 */
export const safeDate = (value?: string | number | Date | null): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  let d: Date;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // Date-only string (no time/zone). `new Date('2026-06-02')` parses as UTC
    // midnight, which renders as the PREVIOUS day in negative-UTC timezones
    // (e.g. a med added today shows "Yesterday"). Build it at LOCAL midnight so
    // a calendar day stays that calendar day everywhere it's displayed.
    const [y, m, day] = value.split('-').map(Number);
    d = new Date(y, m - 1, day);
  } else {
    d = value instanceof Date ? value : new Date(value);
  }
  return isValid(d) ? d : null;
};

const formatRelDate = (iso: string) => {
  const d = safeDate(iso);
  if (!d) return 'Unknown date';
  if (isToday(d)) return `Today · ${format(d, 'h:mm a')}`;
  if (isYesterday(d)) return `Yesterday · ${format(d, 'h:mm a')}`;
  return format(d, 'MMM d, yyyy');
};

const formatExactDate = (iso: string) => {
  const d = safeDate(iso);
  if (!d) return 'Unknown date';
  return `${format(d, 'MMM d, yyyy h:mm a')} · ${formatDistanceToNow(d, { addSuffix: true })}`;
};

/**
 * Map a heterogeneous record from `recordsByType.all` (SOAP notes, medication
 * groups, procedures, vaccinations, raw medical records) onto the uniform
 * TimelineEvent shape the row component renders. Returns null for items we
 * don't know how to render so the caller can skip them safely.
 */
export function toTimelineEvent(item: any): TimelineEvent | null {
  if (!item) return null;

  // SOAP notes — title is the diagnosis or chief complaint, fall back to template name.
  if (item.itemType === 'soap') {
    const dx = item.assessment?.primaryDiagnosis;
    const sub = item.subjective?.aiSummary || item.subjective?.notes;
    const title = dx || (item.templateUsed ? `${item.templateUsed} SOAP` : 'SOAP Note');
    return {
      id: item.id,
      kind: 'soap',
      date: typeof item.date === 'string' ? item.date : (safeDate(item.date)?.toISOString() ?? ''),
      title,
      summary: firstLine(sub) || (item.status === 'DRAFT' ? 'Draft' : undefined),
      by: item.provider?.name,
      isArchived: item.isArchived,
      raw: item,
    };
  }

  // Grouped medications — show drug names and count.
  if (item.itemType === 'medication-group') {
    const meds = item.medications ?? [];
    const first = meds[0];
    const others = meds.length > 1 ? ` +${meds.length - 1} more` : '';
    const title = first ? `${first.name}${others}` : `${meds.length} medication${meds.length === 1 ? '' : 's'}`;
    const summary = first
      ? [first.dosage, first.frequency, first.route].filter(Boolean).join(' · ')
      : undefined;
    return {
      id: item.id,
      kind: 'medication',
      date: item.date,
      title,
      summary,
      by: first?.prescribedBy,
      isArchived: meds.every((m: any) => m.isArchived),
      raw: item,
    };
  }

  // Single medication (rare — usually grouped, but handle for safety).
  if (item.itemType === 'medication' || item.type === 'medication') {
    return {
      id: item.id,
      kind: 'medication',
      date: item.startDate || item.date,
      title: item.name,
      summary: [item.dosage, item.frequency, item.route].filter(Boolean).join(' · '),
      by: item.prescribedBy,
      isArchived: item.isArchived,
      raw: item,
    };
  }

  if (item.itemType === 'vaccination' || item.type === 'vaccination') {
    // A CALENDAR day: its date part, never re-zoned (DATE-KINDS §1.1).
    const nextDue = formatCalendarDate(item.nextDueDate, 'MMM d, yyyy');
    return {
      id: item.id,
      kind: 'vaccination',
      date: item.dateAdministered || item.date,
      title: item.vaccineName || item.name || 'Vaccination',
      summary: nextDue ? `Next due ${nextDue}` : undefined,
      by: item.administeredBy,
      isArchived: item.isArchived,
      raw: item,
    };
  }

  if (item.itemType === 'procedure' || item.type === 'procedure') {
    return {
      id: item.id,
      kind: 'procedure',
      date: item.date,
      title: item.name || 'Procedure',
      summary: firstLine(item.description),
      by: item.performedBy,
      isArchived: item.isArchived,
      raw: item,
    };
  }

  // Discharge summaries.
  if (item.itemType === 'discharge') {
    const date = item.dischargeDateTime || item.admissionDateTime || item.createdAt;
    return {
      id: item.id,
      kind: 'discharge',
      date,
      title: item.status === 'DRAFT' ? 'Discharge Summary (Draft)' : 'Discharge Summary',
      summary: item.attendingVet ? `Vet: ${item.attendingVet}` : undefined,
      by: item.finalizedBy || item.attendingVet,
      raw: item,
    };
  }

  // A StableDocs send (GET /stabledocs/horses/:id/shares).
  if (item.itemType === 'stabledocs') {
    return {
      id: item.id,
      kind: 'stabledocs',
      date: item.sentAt,
      title: `StableDocs sent to ${item.recipientName || item.recipientEmail}`,
      summary: `${item.itemCount} ${item.itemCount === 1 ? 'item' : 'items'} · Attachment`,
      by: item.senderName ?? undefined,
      raw: item,
    };
  }

  // Invoices.
  if (item.itemType === 'invoice') {
    const total = typeof item.total === 'number' ? ` · $${item.total.toFixed(2)}` : '';
    return {
      id: item.id,
      kind: 'invoice',
      date: item.date || item.createdAt,
      title: `Invoice #${item.invoiceNumber || item.id.slice(0, 8)}${total}`,
      summary: item.status ? `Status: ${item.status}` : undefined,
      raw: item,
    };
  }

  // Plain medical records (visit notes + imported docs + the legacy "type" enum).
  const recordType: string = item.type ?? 'note';
  const isVisit = recordType === 'visit_note' || recordType === 'Visit Note';
  const isImported = typeof item.notes === 'string' && item.notes.startsWith('[Imported Document]');
  const importedTitle = isImported
    ? item.notes.split('\n')[0].replace('[Imported Document] ', '').trim()
    : null;

  return {
    id: item.id,
    kind: isVisit ? 'visit_note' : 'record',
    date: item.date,
    title: isImported ? importedTitle || 'Imported Document' : (isVisit ? 'Visit Note' : recordType),
    summary: isImported ? undefined : firstLine(item.notes),
    isArchived: item.isArchived,
    raw: item,
  };
}

export interface TimelineRowProps {
  event: TimelineEvent;
  selected?: boolean;
  onSelect: (event: TimelineEvent) => void;
  onMenu?: (event: TimelineEvent, anchor: { top: number; left: number }) => void;
}

/**
 * Single dense row in the chart timeline. Click → open the detail drawer.
 * The row is intentionally low-chrome: type chip on the left, title +
 * one-line summary in the middle, date / author on the right.
 */
const TimelineRow: React.FC<TimelineRowProps> = ({ event, selected, onSelect, onMenu }) => {
  const meta = kindMeta(event.kind);

  const handleMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!onMenu) return;
    const rect = e.currentTarget.getBoundingClientRect();
    onMenu(event, { top: rect.bottom, left: rect.right });
  };

  return (
    <Box
      onClick={() => onSelect(event)}
      sx={{
        display: 'grid',
        gridTemplateColumns: '92px 60px minmax(0, 1fr) auto 32px',
        alignItems: 'center',
        gap: 1.25,
        py: 0.85,
        px: 1.25,
        cursor: 'pointer',
        borderRadius: 1.5,
        borderLeft: '3px solid transparent',
        opacity: event.isArchived ? 0.55 : 1,
        bgcolor: selected ? 'rgba(201, 119, 43, 0.08)' : 'transparent',
        borderLeftColor: selected ? meta.color : 'transparent',
        '&:hover': {
          bgcolor: selected ? 'rgba(201, 119, 43, 0.12)' : 'rgba(0,0,0,0.03)',
        },
      }}
      data-testid={`timeline-row-${event.id}`}
    >
      {/* Date */}
      <Tooltip title={formatExactDate(event.date)} placement="top-start" arrow>
        <Typography
          variant="caption"
          sx={{
            fontVariantNumeric: 'tabular-nums',
            color: 'text.secondary',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatRelDate(event.date)}
        </Typography>
      </Tooltip>

      {/* Type chip */}
      <Chip
        icon={meta.icon}
        label={meta.label}
        size="small"
        sx={{
          bgcolor: meta.bg,
          color: meta.color,
          height: 22,
          fontSize: '0.7rem',
          fontWeight: 600,
          borderRadius: 1,
          '& .MuiChip-icon': { color: meta.color, ml: 0.5 },
          '& .MuiChip-label': { px: 0.75 },
        }}
      />

      {/* Title + one-line summary */}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {event.title}
          </Typography>
          {event.isArchived && (
            <Tooltip title="Archived">
              <ArchivedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            </Tooltip>
          )}
        </Box>
        {event.summary && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.35,
            }}
          >
            {event.summary}
          </Typography>
        )}
      </Box>

      {/* By */}
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          whiteSpace: 'nowrap',
          maxWidth: 140,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'right',
        }}
      >
        {event.by || ''}
      </Typography>

      {/* Menu */}
      {onMenu ? (
        <IconButton size="small" onClick={handleMenu} sx={{ p: 0.25 }} aria-label="row actions">
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : (
        <Box />
      )}
    </Box>
  );
};

export default TimelineRow;
